/**
 * The entry file names its customer: `<anything>_<customer code>.xlsx`.
 *
 * The browser half of backend_api/upload_names.py: the same pattern and the
 * same comparison (case and surrounding spaces do not count), so a file named
 * for another customer is refused here, with a translated message and no
 * round trip. The last part is the customer's CODE (`SAP0001`); what comes
 * before it is free, so each file keeps its own descriptive name. The server
 * remains the authority.
 */
import type { CustomerSummary } from "./api";

const SUFFIX = /^.+_([^_/\\]+)\.(xlsx|xlsm)$/i;

/** What a file for this customer is called, for messages. */
export function expectedName(code: string): string {
  return `*_${code}.xlsx`;
}

/** The customer code a file name carries; null when the name has no `_part`. */
export function customerCode(filename: string): string | null {
  const match = SUFFIX.exec(filename.trim());
  return match ? match[1] : null;
}

/** `sap0001` ≈ `SAP0001`: case and surrounding spaces do not count. */
export function normalizeCode(text: string): string {
  return text.trim().toLowerCase();
}

/** True when the file carries this customer's code. */
export function matchesCustomer(
  filename: string,
  customer: Pick<CustomerSummary, "code">,
): boolean {
  const found = customerCode(filename);
  if (found == null) return false;
  const wanted = normalizeCode(customer.code);
  return wanted !== "" && normalizeCode(found) === wanted;
}

/**
 * Why a file is refused for a customer: "format" when the name carries no
 * `_part` before a workbook extension, "mismatch" when that part is another
 * customer's code, null when it is fine.
 */
export function uploadNameProblem(
  filename: string,
  customer: Pick<CustomerSummary, "code">,
): { kind: "format" } | { kind: "mismatch"; found: string } | null {
  const found = customerCode(filename);
  if (found == null) return { kind: "format" };
  return matchesCustomer(filename, customer) ? null : { kind: "mismatch", found };
}
